import React, { useRef } from "react";
import PropTypes from "prop-types";
import moment from "moment";

import { withTranslation } from "react-i18next";

import { Box, Button, Grid, Typography } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineSharpIcon from "@mui/icons-material/DeleteOutlineSharp";

import { toast } from "react-toastify";

import DateField from "Components/Common/DatePicker";
import InputFieldText from "Components/Common/InputFieldText";

import { getImageUrl } from "utils";
import getFileExtension from "utils/getFileExtension";

import { UploadImage } from "../FireProtection/style";

import pdfImg from "assets/images/pdf.png";

const emptyCertificate = {
  id: 0,
  renewalNumber: "",
  dateOfIssue: "",
  dueDate: "",
  file: null,
  url: "",
};

const validFileFormats = ["jpg", "png", "jpeg", "pdf"];

const FireSafetyCertificateRenewal = ({ formik, t }) => {
  const {
    values: { fscrCertificates },
    setFieldValue,
  } = formik;
  const ref = useRef();

  const handleAdd = () => {
    setFieldValue("fscrCertificates", [
      ...fscrCertificates,
      { ...emptyCertificate },
    ]);
  };

  const handleRemove = (index) => {
    const updated = fscrCertificates.filter((_, i) => i !== index);
    setFieldValue("fscrCertificates", updated);
  };

  const fileChangeHandler = (file, index) => {
    const fileExtension = getFileExtension(file.name)?.toLowerCase();

    if (!(validFileFormats.includes(fileExtension) && file.size < 10485760)) {
      toast.error(
        "Please upload image or pdf file and size should be less than 10 mb"
      );
      return;
    }

    setFieldValue(`fscrCertificates[${index}].file`, file);
    setFieldValue(`fscrCertificates[${index}].url`, "");
  };

  return (
    <Grid item xs={12} className="border ps-3 pe-2 pb-2 pt-0" sx={{ mb: 3 }}>
      <Grid item xs={12} className="mt-3">
        <Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap" }}>
          <Typography className="mb-3 me-3" variant="h6">
            {t("Fire Safety Certificate Renewal")}
          </Typography>

          <Button
            color="primary"
            className="mb-3"
            variant="outlined"
            onClick={handleAdd}
            sx={{ textTransform: "capitalize" }}
          >
            <strong>+ {t("Add Fire Safety Certificate Renewal")}</strong>
          </Button>
        </Box>
      </Grid>

      {/* List */}
      {!!fscrCertificates.length &&
        fscrCertificates.map(({ dateOfIssue, url, file }, index) => (
          <Grid
            key={index}
            container
            columnSpacing={1}
            rowSpacing={1}
            className="border p-2 pe-3 mt-2"
          >
            <Grid item xs={12} sm={6} md={4} lg={3}>
              <InputFieldText
                label={t("FSCR No.") + "*"}
                name={`fscrCertificates[${index}].renewalNumber`}
                formik={formik}
                placeholder="Enter FSCR No."
              />
            </Grid>

            <Grid item xs={12} sm={6} md={4} lg={3}>
              <DateField
                label={t("Date of Issuance") + "*"}
                name={`fscrCertificates[${index}].dateOfIssue`}
                formik={formik}
                format="DD-MM-YYYY"
                disableFuture
              />
            </Grid>

            <Grid item xs={12} sm={6} md={4} lg={3}>
              <DateField
                label={t("Due Date") + "*"}
                name={`fscrCertificates[${index}].dueDate`}
                formik={formik}
                format="DD-MM-YYYY"
                minDate={
                  dateOfIssue ? moment(dateOfIssue).add(1, "days") : undefined
                }
              />
            </Grid>

            <Grid container rowSpacing={1} columnSpacing={2} sx={{ pl: 1 }}>
              <Grid item>
                <Typography sx={{ pt: 1 }}>{t("Upload Document")}</Typography>
              </Grid>
              <Grid item xs={12} className="mb-3 ms-1 mt-1">
                <div className="d-flex gap-3 flex-wrap">
                  {(url || file) && (
                    <div className="image-container">
                      <a
                        href={
                          url ? getImageUrl(url) : URL.createObjectURL(file)
                        }
                        target="_blank"
                        rel="noreferrer"
                      >
                        <Box
                          component="img"
                          className="image d-block m-auto"
                          alt="document"
                          src={
                            getFileExtension(
                              (url ? url : file.name).toLowerCase()
                            ) === "pdf"
                              ? pdfImg
                              : url
                              ? getImageUrl(url)
                              : URL.createObjectURL(file)
                          }
                        />
                      </a>

                      <i
                        className="ri-close-circle-line fs-3 close-btn"
                        onClick={() => {
                          setFieldValue(
                            url
                              ? `fscrCertificates[${index}].url`
                              : `fscrCertificates[${index}].file`,
                            ""
                          );
                        }}
                      />
                    </div>
                  )}

                  {!(url || file) && (
                    <Box component="label" sx={{ border: "1px dashed black" }}>
                      <UploadImage>
                        <AddIcon sx={{ color: "black" }} />
                      </UploadImage>
                      <input
                        ref={ref}
                        type="file"
                        hidden
                        onChange={(e) =>
                          fileChangeHandler(e.target.files[0], index)
                        }
                      />
                    </Box>
                  )}
                </div>
              </Grid>
            </Grid>

            <Grid item xs={3}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <Button
                  color="error"
                  size="small"
                  sx={{
                    minWidth: "unset",
                    border: "1px solid",
                    height: "40px",
                    width: "100px",
                    mb: 2,
                  }}
                  onClick={() => handleRemove(index)}
                >
                  <strong className="d-flex justify-content-center">
                    <DeleteOutlineSharpIcon fontSize="small" />
                    {t("Remove")}
                  </strong>
                </Button>
              </Box>
            </Grid>
          </Grid>
        ))}
    </Grid>
  );
};

FireSafetyCertificateRenewal.propTypes = {
  formik: PropTypes.object,
  t: PropTypes.func,
};

export default withTranslation()(FireSafetyCertificateRenewal);
